/**
 * モーダルの上下の余白管理
 */
import useCss from '@berlin-front/common/composables/useCss';
import { includes, isIOS } from '@berlin-front/common/utils';
import { computed, onBeforeUnmount, ref, Ref, watch } from '@vue/composition-api';

type UseSpacerParam = {
  /** サービスがactiveかどうか。 */
  isActiveRef: Ref<boolean>;
  /** サービスの初期化パラメータに与えられた追加スペースに関する情報。 */
  additionalSpaceRef?: Ref<{ top?: number; bottom?: number }>;
};

/**
 *
 * @param param
 */
export default function useSpacer(param: UseSpacerParam) {
  const iOS = isIOS();
  const hasFocusInput = ref(false);
  /** iOSのキーボード表示時に追加されるモーダル下部スペースのピクセル数。inputにフォーカスされるとキーボードが表示されているものとみなす。 */
  const iOSKeyboardSpace = computed(() => (param.isActiveRef.value && iOS && hasFocusInput.value ? 344 : 0));
  /** 会員サイトから指定されるモーダル上部の追加スペース */
  const additionalTopSpace = computed(() => (param.isActiveRef ? (param.additionalSpaceRef?.value?.top ?? 0) : 0));
  /** 会員サイトから指定されるモーダル下部の追加スペース */
  const additionalBottomSpace = computed(() =>
    param.isActiveRef ? (param.additionalSpaceRef?.value?.bottom ?? 0) : 0,
  );

  /** モーダル上部のスペースを追加するcss */
  const topSpacerCss = computed(() =>
    additionalTopSpace.value !== 0
      ? /* css */ `
        .NpApp .AppClose::before {
          display: block;
          height: ${additionalTopSpace.value}px;
          content: '';
        }`
      : '',
  );

  /** モーダル下部のスペースを追加するcss */
  const bottomSpacerCss = computed(() => {
    const bottomSpace = additionalBottomSpace.value + iOSKeyboardSpace.value;
    return bottomSpace !== 0
      ? /* css */ `
        .NpApp .wrapper::after {
          display: block;
          height: ${bottomSpace}px;
          content: '';
        }`
      : '';
  });

  // composableによって追加されるcss。上部と下部を合わせたcssをセット。
  useCss(() => bottomSpacerCss.value + topSpacerCss.value);

  // 以下のコードは画面上のフォーカス監視と、フォーカス状態を更新するためのコード。
  const onFocusin = (ev: FocusEvent) => {
    const { target } = ev;
    hasFocusInput.value =
      target !== null &&
      target instanceof HTMLInputElement &&
      includes('text', 'password', 'email', 'tel', 'number', 'url', 'search')(target.type);
  };
  const onFocusout = () => {
    hasFocusInput.value = false;
  };

  /**
   * 入力要素へのフォーカス監視を開始します。
   */
  const startDomFocusObserve = () => {
    const { activeElement } = document;
    hasFocusInput.value = Boolean(
      activeElement &&
        activeElement instanceof HTMLInputElement &&
        includes('text', 'password', 'email', 'tel', 'number', 'url', 'search')(activeElement.type),
    );
    window.document.addEventListener('focusin', onFocusin, { passive: true });
    window.document.addEventListener('focusout', onFocusout, { passive: true });
  };

  /**
   * 入力要素へのフォーカス監視を終了します。
   */
  const stopDomFocusObserve = () => {
    window.document.removeEventListener('focusin', onFocusin);
    window.document.removeEventListener('focusout', onFocusout);
  };
  watch([param.isActiveRef] as const, ([active]) => {
    if (active) {
      startDomFocusObserve();
    } else {
      stopDomFocusObserve();
    }
  });

  // 基本的にアンマウントされることはないと思うけど、その時はdomの監視をストップします。
  onBeforeUnmount(stopDomFocusObserve);
}
